/*
 * Copyright (c) 2021-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {AnimatePresence, motion} from "motion/react";
import {nanoid} from "nanoid";
import DisplayIcon from "../Icons/DisplayIcon.jsx";
import Strings from "toolbox/strings";

class Tabs extends Component {
    constructor(props) {
        super(props);
        this.state = {
            instanceId: nanoid(3),
        }

        this.lastIndex = 0;
        this.tabs = React.createRef();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.activeKey !== this.props.activeKey) {
            let children = Array.isArray(this.props.children) ? this.props.children : [this.props.children]
            let currentIndex = children.findIndex((item) => (item.props.eventKey === this.props.activeKey))

            this.lastIndex = currentIndex;

            let tabs = this.tabs.current
            let index = children.findIndex((item) => (item.props.eventKey === this.props.activeKey))
            let offset = (tabs?.childNodes?.[index]?.offsetLeft - tabs?.offsetLeft) - (tabs?.childNodes?.[index]?.getBoundingClientRect()?.width / 2);
            let lastChildOffset = tabs?.lastChild?.offsetLeft - tabs?.offsetLeft

            // check if the tab is so small that it has to be scrolled
            if (tabs?.getBoundingClientRect().width < lastChildOffset) {
                tabs.scrollTo({left: offset, behavior: 'smooth'});
            }
        }
    }

    render() {
        let {className, activeKey, hiddeNav = false} = this.props

        let children = Array.isArray(this.props.children) ? this.props.children : [this.props.children]
        let currentItems = children.find(tab => (activeKey === tab.props.eventKey))
        let currentIndex = children.findIndex(tab => (activeKey === tab.props.eventKey))

        let direction = 0;
        if (this.lastIndex !== currentIndex) {
            direction = (this.lastIndex >= currentIndex) ? -1 : 1;
        }

        return (
            <div className={`max-w-full overflow-hidden h-full flex flex-col ${className ?? ""}`}>
                {!hiddeNav && (
                    <>
                        <div className="max-w-full overflow-hidden  shrink-0 ">
                            <nav
                                className={`flex space-x-3 overflow-x-auto noScrollbar snap-x snap-mandatory h-11 border-b-1 pl-3 border-stone-200`}
                                aria-label="Tabs"
                                ref={this.tabs}
                            >
                                {children.map((tab, index) => {
                                    let isSelected = (activeKey === tab.props.eventKey)

                                    return (
                                        <div className={"group relative "} key={tab.props.eventKey}>
                                            <div
                                                onClick={() => {
                                                    this.props.onSelect(tab.props.eventKey)
                                                }}
                                                onDragEnter={() => {
                                                    this.timeout = setTimeout(() => {
                                                        this.props.onSelect(tab.props.eventKey)
                                                    }, 600)
                                                }}
                                                onDragLeave={() => {
                                                    clearTimeout(this.timeout)
                                                }}
                                                className={`text-gray-500 flex items-center justify-center rounded-lg min-w-20 hover:text-gray-700 hover:bg-stone-100 whitespace-nowrap py-1.5 px-2 font-medium text-sm cursor-pointer select-none scroll-ml-12 snap-start snap-always ${isSelected && " text-blue-600! hover:bg-blue-50! hover:text-blue-600!   "}`}
                                            >
                                                {!Strings.isEmpty(tab.props.iconName) && (
                                                    <>
                                                        <DisplayIcon iconName={tab.props.iconName} className={"mr-2"}/>
                                                    </>
                                                )}
                                                <span className={"z-10"}>{tab.props.title}</span>
                                            </div>

                                            {!isSelected && (
                                                <div
                                                    className="absolute bottom-0 left-0 right-0 mx-auto w-[60%] h-[2px] bg-gray-300 rounded-t-md opacity-0 group-hover:opacity-100 transition-opacity"
                                                />
                                            )}

                                            {isSelected && (
                                                <motion.span
                                                    layoutId={"tabFeedback::" + this.state.instanceId}
                                                    className="absolute bottom-0 left-0 right-0 mx-auto w-[60%] z-10 h-[2px] bg-blue-500 rounded-t-md"
                                                    transition={{type: "spring", bounce: 0.2, duration: 0.6}}
                                                />
                                            )}
                                        </div>
                                    )
                                })}
                                <div className={"pr-96"}></div>
                            </nav>
                        </div>
                    </>
                )}

                <div className={"shadow-md flex-grow overflow-hidden relative"}>
                    <AnimatePresence initial={false} mode='popLayout'>
                        {currentItems && (
                            <motion.div
                                key={activeKey}
                                className={"w-full h-full  will-change-transform z-0"}
                                transition={{
                                    type: "spring",
                                    stiffness: 350,
                                    damping: 25
                                }}
                                initial={{x: direction * 25 + "%", opacity: 0}}
                                animate={{x: 0, opacity: 1}}
                                exit={{opacity: 0}}
                            >
                                <div key={currentItems.props.eventKey} className={"w-full h-full"}>
                                    {currentItems}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        );
    }
}

Tabs.propTypes = {
    /**
     * key of the the active tob
     */
    activeKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * The key of the
     */
    defaultActiveKey: PropTypes.string,
    onSelect: PropTypes.func,
};

export default Tabs;